import React from 'react';

/**
 * Returns CSS classes/styles for a user's accent colour
 * Used on usernames in posts, comments, leaderboard, etc.
 */
export const getAccentStyle = (accentColor) => {
  if (!accentColor) return {};
  const map = {
    gold:  { color: '#C5A52A' },
    green: { color: '#22c55e' },
    blue:  { color: '#3b82f6' },
    red:   { color: '#ef4444' },
  };
  return map[accentColor] || {};
};

/**
 * CSS class string for name_style
 */
export const getNameStyleClass = (nameStyle) => {
  if (!nameStyle) return '';
  const map = {
    bold:   'font-bold',
    italic: 'italic',
  };
  return map[nameStyle] || '';
};

/**
 * Returns Tailwind class + inline style for profile frame animation
 */
export const getFrameStyle = (profileFrame) => {
  if (!profileFrame) return { className: '', style: {} };
  if (profileFrame === 'gold_glow') {
    return {
      className: 'profile-frame-gold',
      style: {},
    };
  }
  if (profileFrame === 'pulse') {
    return {
      className: 'profile-frame-pulse',
      style: {},
    };
  }
  if (profileFrame === 'silver') {
    return {
      className: 'profile-frame-silver',
      style: {},
    };
  }
  return { className: '', style: {} };
};

/**
 * StyledUsername — renders a username with accent colour + bold/italic
 */
export const StyledUsername = ({ user, className = '', children }) => {
  const accentStyle  = getAccentStyle(user?.accent_color);
  const nameClass    = getNameStyleClass(user?.name_style);
  const displayName  = children || user?.name || user?.username || 'User';

  return (
    <span
      className={`${nameClass} ${className}`}
      style={accentStyle}
    >
      {displayName}
    </span>
  );
};

/**
 * FramedAvatar — renders a profile picture with an animated frame if active
 */
export const FramedAvatar = ({ user, size = 10, className = '' }) => {
  const frame = user?.profile_frame;
  const sizeClass = `w-${size} h-${size}`;

  const inner = user?.profile_image_url ? (
    <img
      src={user.profile_image_url}
      alt={user.name || 'User'}
      className={`${sizeClass} rounded-full object-cover`}
    />
  ) : (
    <div className={`${sizeClass} rounded-full bg-gray-800 flex items-center justify-center text-gray-400 font-bold text-sm`}>
      {(user?.name || user?.username || 'U')[0].toUpperCase()}
    </div>
  );

  if (!frame) {
    return <div className={`${sizeClass} rounded-full overflow-hidden ${className}`}>{inner}</div>;
  }

  return (
    <div className={`relative rounded-full ${className}`} style={{ width: `${size * 4}px`, height: `${size * 4}px` }}>
      <div className={`absolute inset-0 rounded-full profile-frame-${frame === 'gold_glow' ? 'gold' : frame === 'pulse' ? 'pulse' : 'silver'}`} />
      <div className="absolute inset-[3px] rounded-full overflow-hidden">
        {inner}
      </div>
    </div>
  );
};

export default { StyledUsername, FramedAvatar, getAccentStyle, getNameStyleClass, getFrameStyle };
